// frontend/src/components/verse/VerseCard.js
import { useNavigate } from 'react-router-dom';
import Sanscript       from '@indic-transliteration/sanscript';
import { useSetting }  from '../../hooks/useSettings';
import styles          from './VerseCard.module.css';

const TELUGU_SOURCES = [
  'VEMANA', 'NANNAYYA', 'POTANA', 'TIKKANA',
  'ERRAPRAGADA', 'SUMATI', 'DASARATHI',
  'ANNAMACHARYA', 'TYAGARAJA', 'POTHULURI',
];

function getSourceCode(verse) {
  return (verse.source_code || verse.verse_id || '').split('#')[0].toUpperCase();
}

function isTeluguVerse(verse) {
  if (verse.language === 'te') return true;
  return TELUGU_SOURCES.includes(getSourceCode(verse));
}

function toDevanagari(iast) {
  if (!iast) return '';
  try { return Sanscript.t(iast, 'iast', 'devanagari'); }
  catch { return iast; }
}

function resolveDisplay(verse) {
  if (isTeluguVerse(verse)) {
    const primary = verse.original_text || verse.telugu_text || verse.iast_text || '';
    return { primary, secondary: null, primaryClass: 'telugu' };
  }
  const devanagari = verse.original_text || toDevanagari(verse.iast_text);
  const iast       = verse.iast_text || '';
  return { primary: devanagari, secondary: iast, primaryClass: 'devanagari' };
}

export default function VerseCard({ verse, showScore = true }) {
  const navigate    = useNavigate();
  const [showBadge] = useSetting('showSemanticScore');

  const { primary, secondary, primaryClass } = resolveDisplay(verse);
  const isHighScore = (verse.semantic_score || 0) >= 0.85;

  const primaryTextClass =
    primaryClass === 'telugu'     ? styles.teluguText     :
    primaryClass === 'devanagari' ? styles.devanagariText :
                                    styles.iastText;

  return (
    <article
      className={`${styles.card} ${isHighScore ? styles.cardHigh : styles.cardMid}`}
      onClick={() => navigate(`/verse/${encodeURIComponent(verse.verse_id)}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && navigate(`/verse/${encodeURIComponent(verse.verse_id)}`)}
      aria-label={`Verse from ${verse.source_title || verse.source_code}`}
    >
      {showScore && showBadge && verse.semantic_score != null && (
        <div className={styles.scoreBadge}>{verse.semantic_score.toFixed(2)}</div>
      )}
      {primary && (
        <p className={primaryTextClass}>{primary}</p>
      )}
      {secondary && (
        <p className={styles.iastSecondary}>{secondary}</p>
      )}
      {verse.english_translation && (
        <p className={styles.translation}>{verse.english_translation}</p>
      )}
      <footer className={styles.footer}>
        <span className="tag tag--source">{verse.source_title || verse.source_code}</span>
        {verse.chapter && verse.verse_number && (
          <span className={styles.refNum}>{verse.chapter}.{verse.verse_number}</span>
        )}
        {verse.tradition && (
          <span className="tag tag--tradition">{verse.tradition}</span>
        )}
      </footer>
    </article>
  );
}
